import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAppKitConnection } from '@reown/appkit-adapter-solana/react';
import { modal } from '@reown/appkit/react';
import { jwtDecode } from "jwt-decode";
import {
  useAppKitAccount,
  useAppKitProvider,
} from '@reown/appkit/react';

export default function ConnectWallet() {
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_API;

  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    phantomSolanaAddress: "",
  });
  const [errors, setErrors] = useState({});
  const [memberCode, setMemberCode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { walletProvider: solanaProvider } = useAppKitProvider('solana');
  const { isConnected, address } = useAppKitAccount();

  
  const {
    connected,
    connecting,
    disconnecting,
    disconnect,
    error: walletError,
  } = useAppKitConnection('solana');

  // Check mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Validate token on mount
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      navigate('/token/signin');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${baseUrl}/validate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!data.valid) {
          sessionStorage.removeItem('authToken');
          navigate('/token/signin');
        } else {
          const decoded = jwtDecode(token);
          setMemberCode(decoded.memberCode);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        sessionStorage.removeItem('authToken');
        navigate('/token/signin');
      }
    };

    validateToken();
  }, [navigate, baseUrl]);

  // Update form data when wallet address changes
  useEffect(() => {
    if (address) {
      setFormData(prev => ({ ...prev, phantomSolanaAddress: address }));
    } else {
      setFormData(prev => ({ ...prev, phantomSolanaAddress: "" }));
    }
  }, [address]);

  const handleManualWalletInput = (e) => {
    const address = e.target.value;
    setFormData({ ...formData, phantomSolanaAddress: address });
  };

  const handleConnect = async () => {
    try {
      if (connected) {
        await disconnect();
      } else {
        modal.open('solana');
      }
    } catch (err) {
      console.error('Connection error:', err);
      toast.error('Failed to connect wallet');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
      if(!solanaProvider){
        toast.error('Please Solana-compatible wallet');
        return;
      }

    setIsSubmitting(true);

    if (!solanaProvider && !formData.phantomSolanaAddress) {
      toast.error('Please connect a Solana-compatible wallet or enter address manually');
      setIsSubmitting(false);
      return;
    }

    let newErrors = {};
    if (!formData.phantomSolanaAddress) newErrors.wallet = "Wallet address is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    setErrors({});

    try {
      const requestData = {
        memberCode: memberCode,
        phantomSolanaAddress: formData.phantomSolanaAddress,
      };

      const response = await axios.post(
        `${baseUrl}/api/update-wallet-address`, 
        requestData, 
        { headers: { "Content-Type": "application/json" } }
      );

      toast.success("Wallet address updated successfully!");
      navigate("/token/rzto"); 
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update wallet address!";
      toast.error(errorMessage);
      console.error("Update failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="auth-background"></div>
      <div className="bg-overlay"></div>

      <div className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-card">
            <h1 className="auth-title">Connect Your Wallet</h1>
            <p className="auth-subtitle">
              Complete this step by connecting your wallet to buy RZTO token
            </p>

            <form onSubmit={handleSubmit} className="auth-form">
              {/* Wallet Connection */}
              <button
                onClick={handleConnect}
                disabled={connecting || disconnecting}
                type="button"
                className={`wallet-button ${isConnected ? 'connected' : ''}`}
              >
                {connecting ? "Connecting..." : 
                 disconnecting ? "Disconnecting..." : 
                 isConnected ? "Disconnect Wallet" : "Connect solana Wallet"}
              </button>
              
              {isConnected && formData.phantomSolanaAddress && (
                <p className="wallet-address">
                  Wallet Address: {`${formData.phantomSolanaAddress.substring(0, 6)}...${formData.phantomSolanaAddress.substring(formData.phantomSolanaAddress.length - 4)}`}
                </p>
              )}
              
              {/* {isMobile && (
                <div className="form-group">
                  <div className="input-with-icon">
                    <Icon icon="mdi:wallet-outline" className="input-icon" />
                    <input
                      type="text"
                      name="phantomSolanaAddress"
                      className="form-input"
                      placeholder="Or enter wallet address manually"
                      value={formData.phantomSolanaAddress}
                      onChange={handleManualWalletInput}
                      disabled={isConnected}
                    />
                  </div>
                  {errors.wallet && <p className="error-message">{errors.wallet}</p>}
                </div>
              )} */}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!formData.phantomSolanaAddress || isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? "Saving..." : "Save Wallet"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-background {
          background-image: url(/images/main-background.jpg);
          background-size: cover;
          background-position: center;
          min-height: 100vh;
          width: 100%;
          position: fixed;
          top: 0;
          left: 0;
          z-index: -1;
          filter: blur(10px);
        }
        
        .auth-container {
          width: 100%;
          min-height: calc(100vh - 80px);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
          box-sizing: border-box;
        }
        
        .auth-wrapper {
          width: 100%;
          max-width: 500px;
        }
        
        .auth-card {
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .auth-title {
          font-size: 2rem;
          color: #333;
          text-align: center;
          margin-bottom: 10px;
        }
        
        .auth-subtitle {
          color: #666;
          text-align: center;
          margin-bottom: 30px;
        }
        
        .form-group {
          margin-bottom: 20px;
          position: relative;
        }
        
        .input-with-icon {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          font-size: 1.2rem;
          z-index: 2;
        }
        
        .form-input {
          width: 100%;
          padding: 15px 15px 15px 45px;
          border-radius: 8px;
          border: 1px solid #ddd;
          font-size: 1rem;
          box-sizing: border-box;
          position: relative;
          z-index: 1;
        }
        
        select.form-input {
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 16px center;
          background-size: 1em;
          padding-right: 40px;
          border: 1px solid #ddd;
        }
        
        .password-input-container {
          position: relative;
        }
        
        .password-toggle {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #666;
          z-index: 2;
        }
        
        .password-hint {
          font-size: 0.8rem;
          color: #666;
          margin-top: 5px;
          text-align: left;
          padding-left: 10px;
        }
        
        .wallet-button {
          width: 100%;
          padding: 15px;
          border-radius: 8px;
          border: none;
          background-color: #f0b90b;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          margin-bottom: 15px;
          transition: background-color 0.3s;
        }
        
        .wallet-button:hover {
          background-color: #e0a800;
        }
        
        .wallet-button.connected {
          background-color: #28a745;
        }
        
        .wallet-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .wallet-address {
          font-size: 0.8rem;
          color: #666;
          word-break: break-all;
          margin-top: -10px;
          margin-bottom: 15px;
        }
        
        .submit-button {
          width: 100%;
          padding: 15px;
          border-radius: 8px;
          border: none;
          background-color: #f0b90b;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          margin-top: 10px;
          transition: background-color 0.3s;
        }
        
        .submit-button:hover {
          background-color: #e0a800;
        }
        
        .submit-button:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }
        
        .auth-redirect {
          text-align: center;
          margin-top: 20px;
          color: #666;
        }
        
        .auth-link {
          color: #f0b90b;
          text-decoration: none;
          font-weight: 600;
        }
        
        .auth-link:hover {
          text-decoration: underline;
        }
        
        .error-message {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 5px;
          text-align: left;
          padding-left: 10px;
        }
        
        @media (max-width: 768px) {
          .auth-card {
            padding: 20px;
          }
          
          .auth-title {
            font-size: 1.5rem;
          }
          
          .form-input {
            padding: 12px 12px 12px 40px;
          }
          
          .input-icon {
            display:none;
          }
        }
        
        @media (max-width: 480px) {
          .auth-container {
            padding: 10px;
          }
          
          .auth-card {
            padding: 15px;
          }
          
          .form-input {
            padding: 10px 10px 10px 36px;
          }
        }
      `}</style>
    </>
  );
}